/**
 * Services/Solutions Page for ShopKaBot
 */

import { Box, Container, Grid, Typography, Card, CardContent, Button, Stack, Chip, List, ListItem, ListItemIcon, ListItemText } from "@mui/material";
import { useNavigate } from "react-router-dom";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import Seo from "../../seo/Seo";
import { createOrganizationSchema, createWebsiteSchema, createBreadcrumbSchema, createServiceSchema } from "../../seo/utils";
import servicesData from "../../data/services.json";
import ShopKaBotLeadForm from "../Shared/LeadForm";

interface Service {
  id: number;
  slug: string;
  title: string;
  metaDescription: string;
  heroTitle: string;
  description: string;
  features: string[];
  benefits: string[];
  useCases: string[];
  price: string;
  primaryKeyword: string;
}

function ServicesPage() {
  const navigate = useNavigate();
  const services: Service[] = servicesData.services;

  const schema = [
    createOrganizationSchema(),
    createWebsiteSchema(),
    createBreadcrumbSchema([
      { name: "Home", path: "/" },
      { name: "Services", path: "/services" },
    ]),
    ...services.map((service) =>
      createServiceSchema({
        name: service.title,
        description: service.description,
        url: `/services/${service.slug}`,
      })
    ),
  ];

  return (
    <>
      <Seo
        title="AI Automation Services - ShopKaBot"
        description="Explore ShopKaBot's AI automation services: sales agents, customer support, order management, lead qualification, and appointment scheduling."
        path="/services"
        keywords={["AI automation", "WhatsApp agents", "customer support bot", "lead qualification", "sales automation"]}
        schema={schema}
        type="website"
      />

      <Box sx={{ py: { xs: 4, md: 8 } }}>
        <Container maxWidth="lg">
          {/* Header */}
          <Box sx={{ textAlign: "center", mb: 6 }}>
            <Typography variant="h3" component="h1" sx={{ fontWeight: 700, mb: 2 }}>
              Our AI Automation Services
            </Typography>
            <Typography variant="h6" color="textSecondary" sx={{ mb: 4, maxWidth: "600px", mx: "auto" }}>
              Deploy powerful AI agents on WhatsApp to automate your sales, customer support, lead qualification, and more.
            </Typography>
          </Box>

          {/* Services Grid */}
          <Grid container spacing={3} sx={{ mb: 6 }}>
            {services.map((service) => (
              <Grid size={{ xs: 12, md: 6 }} key={service.id}>
                <Card
                  sx={{
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      transform: "translateY(-4px)",
                      boxShadow: 6,
                    },
                  }}
                >
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                      {service.title}
                    </Typography>
                    <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                      {service.description}
                    </Typography>

                    {/* Features */}
                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                      Key Features:
                    </Typography>
                    <List dense sx={{ mb: 2 }}>
                      {service.features.slice(0, 3).map((feature, idx) => (
                        <ListItem key={idx} sx={{ py: 0.25, px: 0 }}>
                          <ListItemIcon sx={{ minWidth: 32 }}>
                            <CheckCircleIcon sx={{ fontSize: 18, color: "success.main" }} />
                          </ListItemIcon>
                          <ListItemText
                            primary={feature}
                            primaryTypographyProps={{ variant: "body2" }}
                          />
                        </ListItem>
                      ))}
                    </List>

                    {/* Benefits */}
                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                      Benefits:
                    </Typography>
                    <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap", gap: 0.5, mb: 2 }}>
                      {service.benefits.slice(0, 3).map((benefit, idx) => (
                        <Chip key={idx} label={benefit} size="small" variant="outlined" />
                      ))}
                    </Stack>

                    {/* Use Cases */}
                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                      Best For:
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      {service.useCases.join(", ")}
                    </Typography>

                    {/* Price */}
                    <Box sx={{ mt: 2, pt: 2, borderTop: "1px solid rgba(0,0,0,0.1)" }}>
                      <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                        Pricing
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 600, color: "primary.main" }}>
                        {service.price}
                      </Typography>
                    </Box>
                  </CardContent>

                  <Box sx={{ p: 2, pt: 0 }}>
                    <Button
                      fullWidth
                      variant="contained"
                      onClick={() => navigate("/contact-us")}
                    >
                      Learn More & Share Requirements
                    </Button>
                  </Box>
                </Card>
              </Grid>
            ))}
          </Grid>

          {/* Lead Form Section */}
          <Box sx={{ my: 6, py: 4, px: 3, backgroundColor: "#f8f9fa", borderRadius: 2 }}>
            <Typography variant="h5" sx={{ mb: 3, fontWeight: 700, textAlign: "center" }}>
              Get Your Customized AI Automation Plan
            </Typography>
            <ShopKaBotLeadForm formSize="md" />
          </Box>

          {/* Comparison Section */}
          <Box sx={{ mt: 6 }}>
            <Typography variant="h5" sx={{ mb: 3, fontWeight: 700, textAlign: "center" }}>
              Why Choose ShopKaBot?
            </Typography>
            <Grid container spacing={2}>
              {[
                { icon: "⚡", title: "Fast Deployment", desc: "Launch agents in hours, not weeks" },
                { icon: "🎯", title: "Accurate", desc: "AI that understands context and nuance" },
                { icon: "💰", title: "Cost Effective", desc: "Save 50%+ on customer support costs" },
                { icon: "🔄", title: "Easy Integration", desc: "Seamlessly works with your existing tools" },
                { icon: "📊", title: "Data Driven", desc: "Real-time analytics and insights" },
                { icon: "🤝", title: "Expert Support", desc: "24/7 support from automation experts" },
              ].map((item, idx) => (
                <Grid size={{ xs: 12, sm: 6, md: 4 }} key={idx}>
                  <Box sx={{ textAlign: "center", p: 2 }}>
                    <Typography sx={{ fontSize: "2rem", mb: 1 }}>{item.icon}</Typography>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                      {item.title}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      {item.desc}
                    </Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Box>
        </Container>
      </Box>
    </>
  );
}

export default ServicesPage;
